import './assets/css/index.css';
import {createGtm} from '@gtm-support/vue-gtm';
import {createI18nInstance} from '@myparcel-frontend/i18n';
import {loadTranslations} from '@myparcel-frontend/i18n/translations/backoffice/loader';
import {createCookieConsent} from '@myparcel-frontend/plugins';
import {createPropositionPlugin} from '@myparcel-frontend/proposition';
import {useSdk} from '@myparcel-frontend/sdk';
import {createApp} from 'vue';
import App from './App.vue';
import {createAuthPlugin} from './auth/createAuth0';
import useAcl from './composables/useAcl';
import {useServiceAccount} from './composables/useServiceAccount/useServiceAccount';
import {getPropositionConfig} from './configs/proposition';
import {createEnvironment} from './env/plugin';
import {useEnvironmentStore} from './env/useEnvironmentStore';
import {createMockServiceWorker} from './mocks/plugin';
import {loadAccountData} from './plugins/account/loadAccountData';
import {createClients} from './plugins/clients/createClients';
import {router} from './router';
import store from './store';
import {version} from '../package.json';
import {loadZodErrorTranslations} from './i18n/i18n';
import {createNewRelic} from './plugins/newRelic/createNewRelic';
import {featureToggles} from '@/utils/features';
import logToMonitor from '@/utils/logging';

window.AppcuesSettings = {
  enableURLDetection: true,
};

const addAppVersionToHead = () => {
  if (!version) return;

  try {
    const meta = document.createElement('meta');
    meta.name = 'app-version';
    meta.content = version;
    document.head.prepend(meta);
  } catch (error) {
    logToMonitor(error as Error, {
      _myparcel_extra_info: 'Failed to inject app-version meta tag during backoffice boot',
      _myparcel_app_version: version,
    });
  }
};

void (async () => {
  addAppVersionToHead();

  // Create the app instance early to be able to use plugins that require the app instance.
  const app = createApp(App)
    // install pinia before other plugins to allow access to the stores in other plugins
    .use(store);

  // load environment variables before other plugins to allow access to the environment in other plugins
  const envPlugin = await createEnvironment();
  app.use(envPlugin).use(createNewRelic()).use(createMockServiceWorker());
  // setup auth0 For this to load we need the environment store to be loaded first
  const auth0Plugin = await createAuthPlugin();
  app.use(auth0Plugin);
  const token = await auth0Plugin.auth0.getAccessTokenSilently();

  const {initSdk, setAcceptLanguageHeader} = useSdk();
  const {environment} = useEnvironmentStore();
  // Pass a token getter as well: auth0 refreshes the cached token when it expires, so
  // long-lived tabs no longer send expired JWTs (see AppErrorBoundary "Invalid JWT" reports).
  initSdk({
    token,
    baseUrl: environment.apiUrl,
    getToken: () => auth0Plugin.auth0.getAccessTokenSilently(),
  });
  // Load the ACL data since this is required to determine which data the user has access to and to prevent unnecessary API calls for data that the user does not have access to. This also ensures that the user's permissions are properly set before loading any other data that may require specific permissions.
  const {getAcl} = useAcl();
  await getAcl(token);

  /**
   * Load the account data.
   * This requires the sdk to be initialized and the ACL data to be loaded to determine the correct account data to load based on the user's permissions and to prevent unnecessary API calls for account data that the user does not have access to.
   */
  const {uiSettings} = await loadAccountData();

  /**
   * Load the feature toggles before the proposition config since some carriers in the proposition config may be filtered based on feature flags.
   */
  app.use(featureToggles);

  /**
   * Load the proposition configuration.
   * This requires account info to be loaded first to determine the correct proposition.
   */
  const proposition = await getPropositionConfig();

  if (!proposition.applications.backoffice.enabled) {
    throw new Error('Backoffice application is not enabled in proposition configuration');
  }

  /**
   * initialize i18n.
   * This requires the proposition and the account data to be loaded to determine the correct language to load based on the user's preferences and the supported languages in the proposition configuration.
   */
  const languageToSet =
    uiSettings?.preferred_language &&
    proposition.applications.backoffice.internationalization.supportedLanguages.includes(uiSettings.preferred_language)
      ? uiSettings.preferred_language
      : proposition.applications.backoffice.internationalization.language;

  const i18n = await createI18nInstance({
    application: 'backoffice',
    locale: languageToSet,
    supportedLocales: proposition.applications.backoffice.internationalization.supportedLanguages,
    loadMessages: loadTranslations,
    onMissing: (locale: string, key: string) => {
      // We report missing translation keys to newRelic on production
      logToMonitor(new Error(`Missing translation key`), {
        _myparcel_extra_info: 'i18n missing translation key',
        _myparcel_i18n_key: key,
        _myparcel_i18n_locale: locale,
      });
    },
  });

  setAcceptLanguageHeader(languageToSet);
  // Custom Zod error translations need to be mapped after i18n is loaded
  loadZodErrorTranslations();

  app
    // attach the proposition plugin after loading the proposition config to allow access to the proposition configuration in other plugins
    .use(createPropositionPlugin(proposition))
    // attach i18n to the vue app instance to allow access to the $i18n instance in other plugins and components
    .use(i18n)
    // load all API clients
    .use(createClients(() => auth0Plugin.auth0.getAccessTokenSilently()));

  /**
   * Load the account data from the whoami endpoint
   */
  const {getAccount: getServiceAccount, initOpenFeature} = useServiceAccount();
  await getServiceAccount();
  await initOpenFeature();

  // Attach the router after OpenFeature is initialized so that route guards
  // can read IAM feature flags correctly on initial (hard) navigation.
  app.use(router);

  if (proposition.tools.cookieConsent.enabled) {
    app.use(
      createCookieConsent({
        type: proposition.tools.cookieConsent.type,
        id: proposition.tools.cookieConsent.id,
      }),
    );
  }

  if (proposition.tools.googleTagManager.enabled && !environment.disableGoogleTagManager) {
    app.use(
      // See https://github.com/gtm-support/vue-gtm for more information.
      createGtm({
        // Your GTM single container ID, array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy'] or array of objects [{id: 'GTM-xxxxxx', queryParams: { gtm_auth: 'abc123', gtm_preview: 'env-4', gtm_cookies_win: 'x'}}, {id: 'GTM-yyyyyy', queryParams: {gtm_auth: 'abc234', gtm_preview: 'env-5', gtm_cookies_win: 'x'}}]
        id: proposition.tools.googleTagManager.id,
        // Add your own serverside GTM script
        source: proposition.tools.googleTagManager.url,
        // Script can be set to `defer` to speed up page load at the cost of less accurate results (in case visitor leaves before script is loaded, which is unlikely but possible). Defaults to false, so the script is loaded `async` by default
        defer: false,
        // Will add `async` and `defer` to the script tag to not block requests for old browsers that do not support `async`
        compatibility: false,
        // defaults to true. Plugin can be disabled by setting this to false for Ex: enabled: !!GDPR_Cookie (optional)
        enabled: !environment.devMode,
        // Whether to display console logs debugs (optional)
        debug: environment.devMode,
        // Whether to load the GTM Script (Helpful if you are including GTM manually, but need the dataLayer functionality in your components) (optional)
        loadScript: true,
        // Whether to call trackView in Vue.nextTick
        trackOnNextTick: false,
        vueRouter: router,
      }),
    );
  }

  app.mount('#app');
})();
